// Central data source for the book "Die Wolkenpost von Funkelmoor" by
// TheKilianCraft. Shared by the promo banner and the dedicated landing page.
//
// --- How to customise later -------------------------------------------------
// 1) Real cover:   set COVER_URL below to the uploaded image URL/path.
// 2) Affiliate:    set AMAZON_TAG to your Amazon partner tag (e.g. 'name-21').
//                  It is appended automatically to every amazon.de link.
// 3) Texts:        edit DESCRIPTION_* and SAMPLE_* strings to the real content.
// ---------------------------------------------------------------------------

// Set to a real image URL/path to replace the stylised placeholder cover.
export const COVER_URL = '/book-cover-funkelmoor.jpg';

// Set to your Amazon partner tag (e.g. 'thekiliancraft-21') to earn commission.
export const AMAZON_TAG = '';

export const BOOK = {
  title: 'Die Wolkenpost von Funkelmoor',
  author: 'TheKilianCraft',
};

// Appends the Amazon affiliate tag to amazon.de links when AMAZON_TAG is set.
export const withAffiliate = (url) => {
  try {
    if (!AMAZON_TAG) return url;
    const u = new URL(url);
    if (!/(^|\.)amazon\.de$/i.test(u.hostname)) return url;
    u.searchParams.set('tag', AMAZON_TAG);
    return u.toString();
  } catch (e) {
    return url;
  }
};

export const AMAZON_PAPERBACK = withAffiliate('https://www.amazon.de/dp/B0HNJMZDM2');

// Fire-and-forget click tracking for the book tip. Uses sendBeacon so it also
// works when the click navigates away (header/menu links). Never throws.
export const trackBookClick = (target) => {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base || !target) return;
    const url = `${base}/api/book-click`;
    const body = JSON.stringify({ target });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else if (typeof fetch !== 'undefined') {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
    }
  } catch (e) { /* tracking must never break the UI */ }
};

// Link to more books by the author (Amazon author search as a sensible default).
export const AUTHOR_MORE_URL = withAffiliate('https://www.amazon.de/s?k=TheKilianCraft');

// Formats grouped by edition, each with its retailer links.
export const FORMATS = [
  {
    id: 'ebook',
    de: { name: 'Kindle eBook', note: 'Exklusiv bei Amazon erhältlich.' },
    en: { name: 'Kindle eBook', note: 'Exclusively available on Amazon.' },
    ids: 'ASIN B0HNGY7X1B',
    shops: [
      { name: 'Amazon', url: withAffiliate('https://www.amazon.de/dp/B0HNGY7X1B') },
    ],
  },
  {
    id: 'paperback',
    de: { name: 'Taschenbuch', note: 'Softcover-Ausgabe.' },
    en: { name: 'Paperback', note: 'Softcover edition.' },
    ids: 'ASIN B0HNJMZDM2 · ISBN 979-8172147326',
    shops: [
      { name: 'Amazon', url: AMAZON_PAPERBACK },
      { name: 'Thalia', url: 'https://www.thalia.de/suche?sq=9798172147326' },
      { name: 'Hugendubel', url: 'https://www.hugendubel.de/de/search?q=9798172147326' },
    ],
  },
  {
    id: 'hardcover',
    de: { name: 'Gebundenes Buch', note: 'Hardcover-Ausgabe.' },
    en: { name: 'Hardcover', note: 'Hardcover edition.' },
    ids: 'ISBN 978-3-696-77320-5',
    shops: [
      { name: 'BoD Buchshop', url: 'https://buchshop.bod.de/die-wolkenpost-von-funkelmoor-thekiliancraft-9783696773205' },
      { name: 'Hugendubel', url: 'https://www.hugendubel.de/de/buch_gebunden/thekiliancraft-die_wolkenpost_von_funkelmoor-55163961-produkt-details.html' },
      { name: 'Amazon', url: withAffiliate('https://www.amazon.de/s?k=9783696773205') },
      { name: 'Thalia', url: 'https://www.thalia.de/suche?sq=9783696773205' },
      { name: 'buecher.de', url: 'https://www.buecher.de/suche/?q=9783696773205' },
      { name: 'Weltbild', url: 'https://www.weltbild.de/suche/9783696773205' },
      { name: 'Osiander', url: 'https://www.osiander.de/suche?sq=9783696773205' },
      { name: 'eBook.de', url: 'https://www.ebook.de/de/tools/searchresult?query=9783696773205' },
      { name: 'Genialokal', url: 'https://www.genialokal.de/Suche/?q=9783696773205' },
      { name: 'Dussmann KulturKaufhaus', url: 'https://www.kulturkaufhaus.de/de/suche?q=9783696773205' },
      { name: 'Saxo.com', url: 'https://www.saxo.com/dk/die-wolkenpost-von-funkelmoor_bog_9783696773205' },
    ],
  },
];

// --- Final copy (edit freely to match the published blurb) ------------------
export const CONTENT = {
  de: {
    tagline: 'Ein zauberhaftes Winter-Vorleseabenteuer über Mut, Freundschaft und die Magie kleiner Augenblicke.',
    description: [
      'Als Mila an einem nebligen Morgen eine winzige Tasche findet, die von innen nach Vanille, Regenluft und Sternenstaub duftet, ahnt sie noch nicht, dass sie zur neuen Aushilfsbotin der Wolkenpost geworden ist.',
      'Gemeinsam mit dem sprechenden Moorfrosch Quaxi muss sie drei verlorene Briefe zurückbringen, bevor der große Mondregen beginnt. Doch in Funkelmoor verschwinden Wege, Bäume flüstern Rätsel, und irgendwo sammelt jemand heimlich alle guten Nachrichten der Welt.',
    ],
    details: [
      ['Genre', 'Kinderbuch / Vorlesebuch'],
      ['Empfohlenes Alter', 'ab 5 Jahren'],
      ['Sprache', 'Deutsch'],
    ],
    sampleTitle: 'Leseprobe',
    charactersTitle: 'Hauptfiguren',
    sampleLangNote: '',
    authorTitle: 'Über den Autor',
    authorBio: [
      'TheKilianCraft schreibt fantasievolle Kindergeschichten, die kleine und große Leser in liebevoll gestaltete Welten entführen. Im Mittelpunkt stehen Figuren mit Herz, leise Botschaften über Mut und Freundschaft und jene kleinen Wunder, die den Alltag verzaubern.',
      'Mit „Die Wolkenpost von Funkelmoor" lädt er dazu ein, gemeinsam zu lesen, zu staunen und ein Stück weit an das Unmögliche zu glauben.',
    ],
    authorBioNote: '',
    authorMoreLabel: 'Weitere Bücher entdecken',
  },
  en: {
    tagline: 'A magical winter read-aloud adventure about courage, friendship and the magic of small moments.',
    description: [
      'When Mila finds a tiny bag on a foggy morning – one that smells of vanilla, rain-washed air and stardust from the inside – she has no idea that she has just become the Cloud Post\u2019s new stand-in messenger.',
      'Together with Quaxi, a talking moor frog, she must return three lost letters before the great Moon Rain begins. But in Funkelmoor paths disappear, trees whisper riddles, and somewhere someone is secretly collecting all the good news in the world.',
    ],
    details: [
      ['Genre', 'Children\u2019s book / read-aloud'],
      ['Recommended age', 'ages 5+'],
      ['Language', 'German'],
    ],
    sampleTitle: 'Reading sample',
    charactersTitle: 'Main characters',
    sampleLangNote: 'The reading sample is shown in the book\u2019s original language (German).',
    authorTitle: 'About the author',
    authorBio: [
      'TheKilianCraft writes imaginative children\u2019s stories that whisk young and old readers away into lovingly crafted worlds. At their heart are characters with warmth, quiet messages about courage and friendship, and the little wonders that make everyday life magical.',
      'With \u201cDie Wolkenpost von Funkelmoor,\u201d the author invites you to read together, to marvel, and to believe just a little in the impossible.',
    ],
    authorBioNote: '',
    authorMoreLabel: 'Discover more books',
  },
};

// Full reading sample (original German text). Rendered as-is on the book page.
export const SAMPLE = {
  characters: [
    'Mila Morgenstern – neugierig, mutig und sehr gut darin, Fragen zu stellen, auch wenn Erwachsene gerade keine Zeit haben.',
    'Quaxi – ein Moorfrosch mit winziger Brille, großem Wortschatz und noch größerem Appetit auf Himbeerbonbons.',
    'Oma Lene – Milas Großmutter, die mehr über das Funkelmoor weiß, als sie zuerst zugibt.',
    'Der Nebelsammler – eine geheimnisvolle Gestalt, die behauptet, traurige Nachrichten seien nützlicher als fröhliche.',
  ],
  chapters: [
    {
      heading: 'Kapitel 1: Die Tasche, die nieste',
      paragraphs: [
        'Mila Morgenstern wusste genau drei Dinge über den Nebel im Moor. Erstens: Er kam meistens dann, wenn man seine Gummistiefel nicht finden konnte. Zweitens: Er roch nach nassen Blättern, kaltem Tee und ein bisschen nach Abenteuer. Drittens: Oma Lene sagte immer, man solle ihm nicht hinterherlaufen.',
        'Natürlich lief Mila ihm trotzdem hinterher.',
        'An diesem Samstagmorgen hatte der Nebel sich wie ein verschlafenes Wollknäuel über den Garten gelegt. Die Apfelbäume waren nur noch graue Schatten, der Zaun war verschwunden, und die alte Regentonne sah aus, als könne sie jeden Moment loshusten.',
        'Normalerweise hätte Mila jetzt vielleicht wirklich gefrühstückt. Oma Lenes Pfannkuchen waren nämlich berühmt, zumindest bei Mila, drei Nachbarskatzen und einem sehr höflichen Igel. Aber heute glitzerte der Nebel nicht nur, er bewegte sich auch in kleinen Pfeilen, als würde er ihr heimlich den Weg zeigen.',
        'Mila beugte sich hinunter. Einer der Tropfen hüpfte auf ihre Fingerspitze und zerplatzte dort lautlos. Für einen winzigen Moment sah sie ein Bild: drei Briefe, ein Frosch mit Brille und ein hoher Turm, der aus Nebel gebaut war. Dann war alles wieder verschwunden.',
        '„Mila! Frühstück!“, rief Oma Lene aus der Küche.',
        '„Gleich!“, rief Mila zurück, was in ihrer Sprache bedeutete: Sobald ich herausgefunden habe, warum der Nebel glitzert.',
        'Sie zog ihre gelben Gummistiefel an, schnappte sich ihren roten Regenmantel und stapfte zum Gartentor. Dort, wo gestern noch ein gewöhnlicher Kiesweg gewesen war, lag heute eine schmale Spur aus funkelnden Tropfen. Jeder Tropfen leuchtete kurz auf, wenn Mila näherkam.',
        '„Das ist bestimmt völlig normal“, murmelte Mila. Sie sagte das immer, wenn etwas ganz und gar nicht normal war.',
        'Die Tropfenspur führte hinter die Brombeerhecke, über den schiefen Holzsteg und direkt an den Rand des Funkelmoors. Mila blieb stehen. Das Moor blubberte leise, als würde es im Schlaf sprechen. Zwischen zwei Grasbüscheln lag etwas Kleines, Hellblaues.',
        'Es war eine Tasche. Nicht größer als ein Pausenbrot, mit silbernen Schnallen und einem winzigen Schildchen: Wolkenpost – bitte nicht schütteln.',
        'Mila hob sie vorsichtig auf.',
        '„Haaaatschi!“',
        'Die Tasche nieste so kräftig, dass drei winzige Umschläge herausflatterten. Einer landete auf Milas Stiefel, einer im Moos, und der dritte schwebte in der Luft, als hätte er vergessen, dass Dinge normalerweise nach unten fallen.',
        '„Na endlich!“, sagte eine quakende Stimme. „Ich dachte schon, die neue Botin kommt nie.“',
        'Mila sah nach unten. Auf einem Pilz saß ein grüner Frosch mit einer runden Brille. Er hielt ein Klemmbrett, dass fast größer war als er selbst.',
        '„Ich?“ Mila zeigte auf sich. „Ich bin keine Botin. Ich bin höchstens gut im Marmeladenbrot-Essen.“',
        '„Das steht hier anders.“ Der Frosch räusperte sich und las vor: „Mila Morgenstern, Alter: neun Jahre, besondere Fähigkeiten: findet Dinge, die andere übersehen; fragt zu viel; rennt nicht weg, wenn Taschen niesen.“',
        '„Ich frage nicht zu viel“, sagte Mila. „Ich frage genau richtig viel.“',
        'Der Frosch nickte. „Ausgezeichnet. Dann fangen wir an. Drei Briefe sind verloren gegangen. Wenn sie bis Mondaufgang nicht zugestellt werden, bleibt Funkelmoor für sieben Jahre ohne gute Nachrichten.“',
        'Mila schluckte. Sie wusste nicht genau, wie viele gute Nachrichten ein Moor brauchte. Aber sieben Jahre ohne klangen eindeutig zu lang.',
        'Mit der niesenden Tasche in den Händen und Quaxi an ihrer Seite machte Mila sich auf den Weg. Der erste Brief sollte zeigen, ob sie wirklich eine Wolkenbotin sein konnte.',
      ],
    },
    {
      heading: 'Kapitel 2: Der erste Brief',
      paragraphs: [
        'Der erste Umschlag war weich wie eine Feder und kitzelte Mila an den Fingern. Auf der Vorderseite stand in goldenen Buchstaben: An den Baum, der vergessen hat zu wachsen.',
        '„Ein Baum kann so etwas vergessen?“, fragte Mila.',
        '„Im Funkelmoor kann man fast alles vergessen“, sagte Quaxi. „Geburtstage, Zaubersprüche, sogar den eigenen Schatten. Aber Wachstum zu vergessen ist besonders unpraktisch.“',
        'Sie folgten einem Pfad aus hellgrünem Moos. Der Pfad bog jedes Mal ab, wenn Mila geradeaus gehen wollte, und einmal machte er eine Schleife nur, um sich selbst zu bewundern.',
        'Bald standen sie vor dem kleinsten Baum, den Mila je gesehen hatte. Er war kaum höher als ein Regenschirm, trug aber eine winzige Krone aus fünf Blättern. „Ich bin bestimmt schon fertig“, piepste der Baum. „Größer werden ist bestimmt furchtbar anstrengend.“',
        'Mila öffnete den Brief. Darin stand nur ein Satz: Du musst nicht sofort riesig sein. Ein neuer Zentimeter ist auch ein Abenteuer.',
        'Der kleine Baum schwieg. Dann streckte er vorsichtig eine Wurzel, hob ein Blatt und wuchs um genau einen Zentimeter. „Huch“, sagte er. „Das hat ja gekribbelt.“',
        'Doch kaum hatte der Baum seinen neuen Zentimeter gefeiert, knackte es unter der Erde. Eine Wurzel hob sich wie ein fragender Finger. „Da unten ist etwas“, piepste der Baum. Mila kniete sich hin und schob das Moos zur Seite. Darunter lag ein winziges Messingschild: Erste Zustellung abgeschlossen. Lage verschlimmert sich trotzdem.',
        'Mila lächelte. Der erste Brief war zugestellt. Doch über dem Moor zog der Nebel plötzlich dunkler zusammen, und irgendwo knarrte eine Stimme: „Gute Nachrichten? Die gehören mir.“',
        'Bevor Mila etwas sagen konnte, begann der Boden unter ihren Stiefeln zu summen. Nicht laut, eher so, als hätte jemand eine Biene in eine Teekanne gesperrt. Der kleine Baum duckte sich, obwohl er dafür eigentlich noch nicht groß genug war. Quaxi riss die Augen auf. „Das ist kein gewöhnlicher Nebel“, flüsterte er. „Das ist Suchnebel. Er findet, was man verstecken will.“',
        'Mila presste die Tasche an sich. Darin raschelten die beiden übrigen Briefe, als hätten sie Angst. „Dann verstecke ich sie eben nicht“, sagte Mila und stellte sich so gerade hin, wie sie konnte. „Ich bringe sie ans Ziel.“ Für einen Augenblick wurde der Nebel dünner. Offenbar mochte Suchnebel keine klaren Antworten.',
        'Doch die erste Zustellung hatte nicht nur einen Baum wachsen lassen. Sie hatte auch etwas geweckt, das gute Nachrichten lieber für sich behalten wollte.',
      ],
    },
  ],
};
